import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { CreateOfferInput, useCreateOffer } from '@/lib/api/offers'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { OfferForm } from '@/components/form/offer/offer-form'
import { offerFormSchema, type OfferFormData } from '@/components/form/offer/validation'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

/**
 * Page for creating a new offer
 */
const CreateOfferPageInner = () => {
    const { t } = useTranslation(['stores', 'common'])

    const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
    const { toast } = useToast()

    const createOfferMutation = useCreateOffer()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const methods = useForm<OfferFormData>({
        defaultValues: {
            titleEn: '',
            type: 'club',
            expiryDate: undefined,
            lineItems: [],
            promotionIds: [],
        },
        resolver: zodResolver(offerFormSchema),
        mode: 'onBlur',
    })

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods


    const handleCreateOffer = async (data: OfferFormData) => {
        try {
            setFormError(null)

            const input: CreateOfferInput = {
                storeId,
                type: data.type,
                titleTrans: {
                    en: data.titleEn,
                },
                expiryDate: data.expiryDate ? new Date(data.expiryDate) : undefined,
                lineItems: data.lineItems.map((item) => ({
                    priceId: item.priceId,
                    quantity: item.quantity,
                    variantId: item.variantId,
                })),
                promotionIds: data.promotionIds,
            }

            await createOfferMutation.mutateAsync(input)

            toast({
                title: t('common:success'),
                description: t('stores:offerCreatedSuccess'),
            })
            navigate(storeRoutes.OFFERS())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: t('common:error'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleCreateOffer)}>
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <BackButton to={storeRoutes.OFFERS()} />
                                <div>
                                    <h1 className="text-2xl font-semibold">{t('stores:createOffer')}</h1>
                                    <p className="text-muted-foreground text-sm">{t('stores:createOfferDescription')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate(storeRoutes.OFFERS())}
                                    disabled={isSubmitting}
                                >
                                    {t('common:cancel')}
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? t('stores:creating') : t('stores:createOffer')}
                                </Button>
                            </div>
                        </div>

                        {formError && !formError.isValidationError && (
                            <div className="mb-6">
                                <ErrorDisplay error={formError} />
                            </div>
                        )}

                        <OfferForm />
                    </form>
                </FormProvider>
            </div>
        </div>
    )
}

export const CreateOfferPage = withRequiredPermission(CreateOfferPageInner, PERMISSIONS.STORES_MANAGE)
export default CreateOfferPage

